import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { CircularProgress } from '@mui/material';
import { useAdminContext } from '../AdminContext';

export default function AwardsTab() {
  const {
    isLoadingAwards,
    awards,
    selectedAwardCode,
    setSelectedAwardCode,
    setNewAwardUsername,
    newAwardUsername,
    subreddit,
    handleRemoveAward,
    handleAssignAward,
    CDN_BASE_URL,
  } = useAdminContext();

  if (isLoadingAwards) return <CircularProgress />;
  if (awards.length === 0) return <Alert severity="info">No award flairs found. Mark flairs as awards in the Flair table.</Alert>;

  const selectedAward = selectedAwardCode ? awards.find((a: any) => (a.flair_code || a.image_flair_code) === selectedAwardCode) : null;
  let imageUrl: string | null = null;
  if (selectedAward && subreddit) {
    const imageSource = selectedAward.image_source || '';
    const imageName = selectedAward.image_name || '';
    if (imageSource) {
      const cleanSource = imageSource.replace('.png', '').trim();
      const imageNameOnly = cleanSource.includes('/') ? cleanSource.split('/').pop() : cleanSource;
      imageUrl = `${CDN_BASE_URL}/60x60/${subreddit}/${imageNameOnly}.png`;
    } else if (imageName) {
      const cleanName = imageName.replace('.png', '').trim().toLowerCase().replace(/\s+/g, '');
      imageUrl = `${CDN_BASE_URL}/60x60/${subreddit}/${cleanName}.png`;
    }
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>Awards Management</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Manage award flairs and assign them to users.</Typography>

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel shrink sx={{ backgroundColor: 'background.paper', px: 1 }}>Select Award</InputLabel>
        <Select
          native
          value={selectedAwardCode || ''}
          onChange={(e) => {
            setSelectedAwardCode(e.target.value as string);
            setNewAwardUsername('');
          }}
          label="Select Award"
          sx={{ '& select': { paddingTop: '16.5px', paddingBottom: '16.5px' } }}
        >
          <option value="" disabled style={{ display: 'none' }}></option>
          {awards.map((award: any, idx: number) => {
            const flairCode = award.flair_code || award.image_flair_code;
            return (
              <option key={idx} value={flairCode}>{award.flair_name || award.image_name || flairCode}</option>
            );
          })}
        </Select>
      </FormControl>

      {selectedAwardCode && selectedAward && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            {imageUrl && (
              <Box
                component="img"
                src={imageUrl}
                alt={selectedAward.flair_name || selectedAward.image_name}
                sx={{ width: 60, height: 60, objectFit: 'contain', border: '1px solid', borderColor: 'divider', borderRadius: 1, backgroundColor: 'background.paper' }}
              />
            )}
            <Typography variant="h6">{selectedAward.flair_name || selectedAward.image_name || selectedAwardCode}</Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>Assigned Users ({selectedAward.users?.length || 0})</Typography>
            <TableContainer sx={{ maxHeight: 400, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Username</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedAward.users && selectedAward.users.length > 0 ? (
                    selectedAward.users.map((u: any, uIdx: number) => (
                      <TableRow key={uIdx} hover>
                        <TableCell>{u.username}</TableCell>
                        <TableCell align="right">
                          <Button size="small" color="error" onClick={() => handleRemoveAward(selectedAwardCode, u.username)}>Remove</Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={2} align="center" sx={{ py: 3 }}>
                        <Typography variant="body2" color="text.secondary">No users assigned</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              size="small"
              placeholder="Username or comma-separated list (e.g. user1, user2, user3)"
              value={newAwardUsername}
              onChange={(e) => setNewAwardUsername(e.target.value)}
              sx={{ flexGrow: 1 }}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && newAwardUsername) handleAssignAward(selectedAwardCode, newAwardUsername);
              }}
            />
            <Button variant="contained" onClick={() => newAwardUsername && handleAssignAward(selectedAwardCode, newAwardUsername)} disabled={!newAwardUsername}>
              Assign
            </Button>
          </Box>
        </Box>
      )}

      {!selectedAwardCode && (
        <Alert severity="info">Select an award from the dropdown above to view and manage assigned users.</Alert>
      )}
    </Paper>
  );
}
